import React, { FC, useEffect, useState } from 'react'
import clsx from 'clsx'

// Components
import { ModalBackButton } from './components/ModalBackButton'
import { ConfirmProfileDetailsCard } from './components/ConfirmProfileDetailsCard'
import MainModal from './common/MainModal'

// Store
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  riderProfileRegistrationActions,
  selectRiderProfileEventId,
  selectRiderProfileHorse,
  selectRiderProfileNewTeamRider,
  selectRiderProfileRider,
  selectRiderProfileTeam,
  selectRiderProfileType,
} from '../../store/riderProfileRegistration/riderProfileRegistrationSlice'

// Helpers
import useToasterHelper from '../../helpers/ToasterHelper'
import { getUserFullName } from '../../helpers/helpers'

// Hooks
import { useCreate } from '../../hooks/useCreate'

// Types
import { ECompetitorTypes } from '../../types/competitor_types'

// Const
import { MODAL_CONSTS } from '../../const/modal-const'
import { MESSAGES_CONST } from '../../const/messages-const'

interface ConfirmProfileDetailsProps {
  handleModal: (showHide: boolean, typeOfModal: string, data?: any) => void
}

export const ConfirmProfileDetails: FC<ConfirmProfileDetailsProps> = ({ handleModal }) => {
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()

  const riderProfileType = useAppSelector(selectRiderProfileType)
  const riderProfileNewTeamRider = useAppSelector(selectRiderProfileNewTeamRider)
  const riderProfileTeam = useAppSelector(selectRiderProfileTeam)
  const riderProfileRider = useAppSelector(selectRiderProfileRider)
  const riderProfileHorse = useAppSelector(selectRiderProfileHorse)
  const riderProfileEventId = useAppSelector(selectRiderProfileEventId)

  const [loading, setLoading] = useState(false)
  const [profilePicture, setProfilePicture] = useState('')
  const [horsePicture, setHorsePicture] = useState('')
  const [newRiderPicture, setNewRiderPicture] = useState('')

  const { createRiderProfile } = useCreate()

  useEffect(() => {
    let imageUrl = ''
    if (riderProfileRider?.picture) {
      imageUrl = URL.createObjectURL(riderProfileRider.picture)
    }
    setProfilePicture(imageUrl)
  }, [riderProfileRider?.picture])

  useEffect(() => {
    let imageUrl = ''
    if (riderProfileHorse?.horsePicture) {
      imageUrl = URL.createObjectURL(riderProfileHorse.horsePicture)
    }
    setHorsePicture(imageUrl)
  }, [riderProfileHorse?.horsePicture])

  useEffect(() => {
    let imageUrl = ''
    if (riderProfileNewTeamRider?.picture) {
      imageUrl = URL.createObjectURL(riderProfileNewTeamRider.picture)
    }
    setNewRiderPicture(imageUrl)
  }, [riderProfileNewTeamRider?.picture])

  const closeHandler = () => {
    handleModal(false, MODAL_CONSTS.CONFIRM_PROFILE_DETAILS)
    dispatch(riderProfileRegistrationActions.clear)
    setTimeout(() => {
      handleModal(true, MODAL_CONSTS.USER_CARD)
    }, 500)
  }
  const onSubmit = async () => {
    setLoading(true)

    try {
      await createRiderProfile({
        riderProfileRider: riderProfileRider?.signupForm,
        riderProfileHorse,
        riderProfileTeam,
        riderProfileNewTeam: riderProfileNewTeamRider
          ? {
              picture: riderProfileNewTeamRider?.picture || null,
              email: riderProfileNewTeamRider.inputData.email.value,
              userName: riderProfileNewTeamRider.inputData.userName.value,
              userFullName: riderProfileNewTeamRider.inputData.userFullName.value,
              date: riderProfileNewTeamRider.inputData.date.value,
              nationality: riderProfileNewTeamRider.inputData.nationality.value,
              discipline: riderProfileNewTeamRider.inputData.discipline.value,
            }
          : null,
        riderProfileEventId,
      })
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
    }
  }

  const backHandler = () => {
    handleModal(false, MODAL_CONSTS.CONFIRM_PROFILE_DETAILS)
    if (riderProfileNewTeamRider) {
      handleModal(true, MODAL_CONSTS.ADD_NEW_TEAM_MEMBER)
    } else if (riderProfileType === ECompetitorTypes.teamRider && !riderProfileNewTeamRider) {
      handleModal(true, MODAL_CONSTS.REGISTER_TEAM, {
        userTeamMembers: riderProfileTeam,
      })
    } else {
      handleModal(true, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER, {
        backHandler: () => {
          handleModal?.(false, MODAL_CONSTS.ADD_NEW_HORSE_MEMBER)
          handleModal?.(true, MODAL_CONSTS.CREATE_RIDER_PROFILE_STEP_2)
        },
      })
    }
  }

  return (
    <MainModal
      title="Confirm Profile Details"
      description={'Confirm team is complete to start registering for events'}
      show={true}
      type="CONFIRM_PROFILE_DETAILS"
      size={'lg'}
      goBackBtn={<ModalBackButton onClick={backHandler} />}
      buttons={[
        {
          label: 'FINISH',
          className: '!w-full',
          loading,
          onClick: onSubmit,
        },
        {
          label: 'CANCEL',
          bgClass: 'bg-SeabiscuitLightThemeColor',
          className: 'outline-none',
          loading,
          borderClass: 'border border-transparent',
          textClass: 'text-SeabiscuitLightTextColor !w-full',
          onClick: closeHandler,
        },
      ]}
    >
      <div className={'-ml-2 -mr-2 text-SeabiscuitDark200ThemeColor pt-5'}>
        <h3 className={'font-medium'}>You and your horse</h3>
        <div
          className={clsx(
            'grid grid-cols-1 md:grid-cols-2 gap-6 mt-2.5 pb-6  border-SeabiscuitGray500ThemeColor ',
            {
              'border-b': riderProfileType === ECompetitorTypes.teamRider,
            }
          )}
        >
          {riderProfileRider && (
            <ConfirmProfileDetailsCard
              title={riderProfileRider.signupForm.fullName}
              subTitle={'@' + riderProfileRider.signupForm.userName}
              src={profilePicture}
            />
          )}
          {riderProfileHorse && (
            <ConfirmProfileDetailsCard
              title={riderProfileHorse.inputData.name.value.toString()}
              subTitle={riderProfileHorse.inputData.breed.value.toString()}
              src={horsePicture}
            />
          )}
        </div>
        {riderProfileType === ECompetitorTypes.teamRider && (
          <>
            <h3 className={'font-medium mt-6'}>Team members</h3>
            <div className={'grid grid-cols-1 md:grid-cols-2 gap-6 pt-6'}>
              {riderProfileNewTeamRider && (
                <ConfirmProfileDetailsCard
                  title={riderProfileNewTeamRider.inputData.userFullName.value.toString()}
                  subTitle={'@' + riderProfileNewTeamRider.inputData.userName.value.toString()}
                  src={newRiderPicture || ''}
                />
              )}
              {riderProfileTeam.map((user) => (
                <ConfirmProfileDetailsCard
                  key={user.id}
                  title={getUserFullName(user)}
                  subTitle={'@' + user.userName}
                  src={user.userProfilePicture || ''}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </MainModal>
  )
}
